import React, { Component } from "react";
import { Button, Alert, Form, Card, Badge, Modal } from "react-bootstrap";
import configData from "../../../config.json";

export default class FieldMappingStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tableStructure: null,
      mappings: props.session.mappings || {},
      duplicateCheckFields: props.session.duplicateCheckFields || [],
      loading: false,
      showTemplateModal: false,
      templates: [],
      selectedTemplate: "",
      templateName: "",
      templateDescription: "",
      saveAsTemplate: false,
      makePublic: false,
    };
  }

  componentDidMount() {
    console.log("FieldMappingStep - Session object:", this.props.session);
    console.log("FieldMappingStep - Headers:", this.props.session.headers);
    this.fetchTableStructure();
    this.fetchTemplates();
  }

  fetchTableStructure = () => {
    const { targetTable } = this.props.session;
    if (!targetTable) return;
    
    this.setState({ loading: true });

    const formData = new FormData();
    formData.append("table_name", targetTable);

    fetch(configData.api_url + "C_BulkImport/get_table_structure", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        this.setState({ loading: false });
        
        if (json.status === "success") {
          this.setState({ tableStructure: json });
          
          // Auto-suggest mappings if no mappings exist
          if (Object.keys(this.state.mappings).length === 0) {
            this.autoSuggestMappings(json);
          }
        } else {
          this.props.onError(json.message || "Failed to fetch table structure");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ loading: false });
        this.props.onError("Network error occurred");
      });
  };

  fetchTemplates = () => {
    const { targetTable } = this.props.session;
    if (!targetTable) return;

    fetch(`${configData.api_url}C_BulkImport/get_templates?table_name=${targetTable}`)
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "success") {
          this.setState({ templates: json.templates || [] });
        }
      })
      .catch((error) => {
        console.error("Error fetching templates:", error);
      });
  };

  autoSuggestMappings = (tableStructure) => {
    const { headers } = this.props.session;
    const columns = tableStructure.columns || [];
    const suggestedMappings = {};

    headers.forEach((header) => {
      const headerName = header.clean_name || header.name.toLowerCase();
      
      // Find exact match first
      const exactMatch = columns.find(col => 
        col.COLUMN_NAME.toLowerCase() === headerName
      );
      
      if (exactMatch) {
        suggestedMappings[exactMatch.COLUMN_NAME] = header.index;
        return;
      }
      
      // Find partial match
      const partialMatch = columns.find(col => 
        col.COLUMN_NAME.toLowerCase().includes(headerName) ||
        headerName.includes(col.COLUMN_NAME.toLowerCase())
      );
      
      if (partialMatch) {
        suggestedMappings[partialMatch.COLUMN_NAME] = header.index;
      }
    });

    this.setState({ mappings: suggestedMappings });
  };

  handleMappingChange = (dbField, excelColumnIndex) => {
    const newMappings = { ...this.state.mappings };
    
    if (excelColumnIndex === "" || excelColumnIndex === null) {
      delete newMappings[dbField];
    } else {
      // Remove any existing mapping to this Excel column
      Object.keys(newMappings).forEach(key => {
        if (newMappings[key] === parseInt(excelColumnIndex)) {
          delete newMappings[key];
        }
      });
      
      newMappings[dbField] = parseInt(excelColumnIndex);
    }
    
    this.setState({ mappings: newMappings });
  };

  handleDuplicateCheckChange = (field, checked) => {
    const { duplicateCheckFields } = this.state;
    
    if (checked) {
      this.setState({
        duplicateCheckFields: [...duplicateCheckFields, field]
      });
    } else {
      this.setState({
        duplicateCheckFields: duplicateCheckFields.filter(f => f !== field)
      });
    }
  };

  saveMappings = () => {
    const { mappings, duplicateCheckFields, saveAsTemplate, templateName, templateDescription, makePublic } = this.state;
    
    if (Object.keys(mappings).length === 0) {
      this.props.onError("Please map at least one field");
      return;
    }

    // Check required fields (excluding automatic fields)
    const { tableStructure } = this.state;
    const automaticFields = ['createdBy', 'updatedBy', 'status', 'createdOn', 'updatedOn', 'created_on', 'updated_on'];
    const requiredFields = tableStructure.columns.filter(col => 
      col.IS_NULLABLE === 'NO' && 
      !col.EXTRA.includes('auto_increment') &&
      !automaticFields.includes(col.COLUMN_NAME)
    );
    
    const missingRequired = requiredFields.filter(field => 
      !mappings.hasOwnProperty(field.COLUMN_NAME)
    );
    
    if (missingRequired.length > 0) {
      this.props.onError(
        `Please map required fields: ${missingRequired.map(f => f.COLUMN_NAME).join(', ')}`
      );
      return;
    }

    const formData = new FormData();
    formData.append("session_id", this.props.session.sessionId);
    formData.append("target_table", this.props.session.targetTable);
    formData.append("mappings", JSON.stringify(mappings));
    formData.append("duplicate_check_fields", JSON.stringify(duplicateCheckFields));
    formData.append("sId", sessionStorage.getItem("sessionId"));
    formData.append("uId", sessionStorage.getItem("uId"));
    
    if (saveAsTemplate && templateName.trim()) {
      formData.append("save_as_template", "1");
      formData.append("template_name", templateName.trim());
      formData.append("template_description", templateDescription.trim());
      formData.append("is_public", makePublic ? "1" : "0");
    }

    this.setState({ loading: true });

    fetch(configData.api_url + "C_BulkImport/save_mapping", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        this.setState({ loading: false });
        
        if (json.status === "success") {
          this.props.onUpdate({
            mappings,
            duplicateCheckFields,
            status: 2, // 2 = validating
          });
          
          const message = saveAsTemplate ? 
            "Mappings saved and template created successfully!" : 
            "Mappings saved successfully!";
          this.props.onSuccess(message);
          this.props.onNext();
        } else {
          this.props.onError(json.message || "Failed to save mappings");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ loading: false });
        this.props.onError("Network error occurred");
      });
  };

  loadTemplate = () => {
    const { selectedTemplate } = this.state;
    if (!selectedTemplate) return;

    fetch(`${configData.api_url}C_BulkImport/load_template?template_id=${selectedTemplate}`)
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "success") {
          const template = json.template;
          const mappings = JSON.parse(template.mapping_config);
          const duplicateCheckFields = JSON.parse(template.duplicate_check_config || '[]');
          
          this.setState({ 
            mappings,
            duplicateCheckFields,
            showTemplateModal: false,
            selectedTemplate: "",
          });
          
          this.props.onSuccess(`Template "${template.template_name}" loaded successfully!`);
        } else {
          this.props.onError(json.message || "Failed to load template");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.props.onError("Network error occurred");
      });
  };

  getMappedExcelColumn = (dbField) => {
    const columnIndex = this.state.mappings[dbField];
    if (columnIndex !== undefined) {
      const header = this.props.session.headers[columnIndex];
      return header ? header.name : null;
    }
    return null;
  };

  getUnmappedExcelColumns = () => {
    const { headers } = this.props.session;
    const mappedIndexes = Object.values(this.state.mappings);
    
    return headers.filter(header => !mappedIndexes.includes(header.index));
  };

  renderMappingRow = (column) => {
    const { mappings } = this.state;
    const { headers } = this.props.session;
    const automaticFields = ['createdBy', 'updatedBy', 'status', 'createdOn', 'updatedOn', 'created_on', 'updated_on'];
    
    // Skip automatic fields - they won't be shown in UI
    if (automaticFields.includes(column.COLUMN_NAME)) {
      return null;
    }
    
    const isRequired = column.IS_NULLABLE === 'NO' && 
                      !column.EXTRA.includes('auto_increment') &&
                      !automaticFields.includes(column.COLUMN_NAME);
    const isMapped = mappings.hasOwnProperty(column.COLUMN_NAME);
    const mappedColumn = this.getMappedExcelColumn(column.COLUMN_NAME);

    return (
      <div key={column.COLUMN_NAME} className="mapping-row">
        <div className="excel-column">
          <div className="d-flex align-items-center">
            <strong>{column.COLUMN_NAME}</strong>
            {isRequired && <span className="required-indicator">*</span>}
          </div>
          <div className="small text-muted">
            {column.COLUMN_TYPE}
            {column.COLUMN_COMMENT && (
              <span> - {column.COLUMN_COMMENT}</span>
            )}
          </div>
        </div>
        
        <div className="mapping-arrow">
          <i className="fas fa-arrow-right"></i>
        </div>
        
        <div className="db-field-select">
          <Form.Select
            value={mappings[column.COLUMN_NAME] || ""}
            onChange={(e) => this.handleMappingChange(column.COLUMN_NAME, e.target.value)}
            size="sm"
          >
            <option value="">-- Select Excel Column --</option>
            {headers && headers.length > 0 ? 
              headers.map((header) => (
                <option key={header.index} value={header.index}>
                  {header.name}
                </option>
              )) : 
              <option disabled>No headers available</option>
            }
          </Form.Select>
          
          {isMapped && (
            <div className="mt-2">
              <Form.Check
                type="checkbox"
                label={
                  <span className="small">
                    <i className="fas fa-clone me-1"></i>
                    Use for duplicate checking
                    {this.state.duplicateCheckFields.length > 0 && 
                     !this.state.duplicateCheckFields.includes(column.COLUMN_NAME) && (
                      <span className="text-muted ms-1">(combine with other fields)</span>
                    )}
                  </span>
                }
                checked={this.state.duplicateCheckFields.includes(column.COLUMN_NAME)}
                onChange={(e) => this.handleDuplicateCheckChange(column.COLUMN_NAME, e.target.checked)}
                size="sm"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { tableStructure, mappings, loading, templates, showTemplateModal } = this.state;
    const { headers } = this.props.session;

    if (loading && !tableStructure) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="mt-2">Loading table structure...</div>
        </div>
      );
    }

    if (!tableStructure) {
      return (
        <Alert variant="warning">
          Failed to load table structure. Please go back and select a table.
        </Alert>
      );
    }

    const columns = tableStructure.columns || [];
    const mappedCount = Object.keys(mappings).length;
    const automaticFields = ['createdBy', 'updatedBy', 'status', 'createdOn', 'updatedOn', 'created_on', 'updated_on'];
    const requiredFields = columns.filter(col => 
      col.IS_NULLABLE === 'NO' && 
      !col.EXTRA.includes('auto_increment') &&
      !automaticFields.includes(col.COLUMN_NAME)
    );
    
    // Filter out automatic fields from display
    const visibleColumns = columns.filter(col => !automaticFields.includes(col.COLUMN_NAME));
    const mappedRequired = requiredFields.filter(field => 
      mappings.hasOwnProperty(field.COLUMN_NAME)
    ).length;
    const unmappedColumns = this.getUnmappedExcelColumns();

    return (
      <div>
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="mb-2">Map Excel Columns to Database Fields</h4>
            <p className="text-muted">
              Connect your Excel columns to the appropriate database fields. Required fields must be mapped.
            </p>
          </div>
        </div>

        {/* Progress Summary */}
        <div className="row mb-4">
          <div className="col-md-8">
            <div className="d-flex gap-4">
              <div>
                <div className="h5 text-primary mb-0">{mappedCount}</div>
                <div className="small text-muted">Fields Mapped</div>
              </div>
              <div>
                <div className="h5 text-success mb-0">{mappedRequired}</div>
                <div className="small text-muted">Required Mapped</div>
              </div>
              <div>
                <div className="h5 text-warning mb-0">{requiredFields.length - mappedRequired}</div>
                <div className="small text-muted">Required Missing</div>
              </div>
              <div>
                <div className="h5 text-info mb-0">{this.state.duplicateCheckFields.length}</div>
                <div className="small text-muted">Duplicate Check</div>
              </div>
            </div>
          </div>
          <div className="col-md-4 text-end">
            {templates.length > 0 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => this.setState({ showTemplateModal: true })}
                className="me-2"
              >
                <i className="fas fa-template me-1"></i>
                Load Template
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => this.autoSuggestMappings(tableStructure)}
            >
              <i className="fas fa-magic me-1"></i>
              Auto-Suggest
            </Button>
          </div>
        </div>

        {/* Field Mappings */}
        <div className="mapping-container">
          <div className="mapping-header">
            <h6 className="mb-0">Field Mappings</h6>
            <small className="text-muted">
              * Required fields must be mapped
            </small>
          </div>
          
          {visibleColumns.map(column => this.renderMappingRow(column)).filter(row => row !== null)}
        </div>

        {/* Duplicate Check Configuration */}
        {this.state.duplicateCheckFields.length > 0 && (
          <Alert variant="warning" className="mt-3">
            <h6 className="alert-heading">
              <i className="fas fa-clone me-2"></i>
              Duplicate Check Configuration
            </h6>
            <p className="mb-2">
              Records will be checked for duplicates based on the combination of these fields:
            </p>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {this.state.duplicateCheckFields.map(field => (
                <Badge key={field} bg="primary">
                  <i className="fas fa-key me-1"></i>
                  {field}
                </Badge>
              ))}
            </div>
            <p className="mb-0 small text-muted">
              <i className="fas fa-info-circle me-1"></i>
              {this.state.duplicateCheckFields.length > 1 
                ? `The system will check if records with the same combination of ${this.state.duplicateCheckFields.join(' + ')} already exist. Only unique combinations will be imported.`
                : `The system will check if records with the same ${this.state.duplicateCheckFields[0]} already exist.`
              }
            </p>
          </Alert>
        )}

        {/* Unmapped Excel Columns */}
        {unmappedColumns.length > 0 && (
          <Alert variant="info" className="mt-3">
            <h6 className="alert-heading">
              <i className="fas fa-info-circle me-2"></i>
              Unmapped Excel Columns
            </h6>
            <p className="mb-2">
              The following columns from your Excel file are not mapped and will be ignored:
            </p>
            <div className="d-flex flex-wrap gap-2">
              {unmappedColumns.map(header => (
                <Badge key={header.index} bg="secondary">
                  {header.name}
                </Badge>
              ))}
            </div>
          </Alert>
        )}

        {/* Template Options */}
        <Card className="mt-4 border-warning">
          <Card.Body>
            <div className="d-flex align-items-center mb-3">
              <Form.Check
                type="checkbox"
                label="Save as template for future use"
                checked={this.state.saveAsTemplate}
                onChange={(e) => this.setState({ saveAsTemplate: e.target.checked })}
              />
            </div>
            
            {this.state.saveAsTemplate && (
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Template Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter template name"
                      value={this.state.templateName}
                      onChange={(e) => this.setState({ templateName: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Description (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Describe this template"
                      value={this.state.templateDescription}
                      onChange={(e) => this.setState({ templateDescription: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Check
                    type="checkbox"
                    label="Make this template available to other users"
                    checked={this.state.makePublic}
                    onChange={(e) => this.setState({ makePublic: e.target.checked })}
                  />
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Continue Button */}
        <div className="text-center mt-4">
          <Button
            variant="primary"
            size="lg"
            onClick={this.saveMappings}
            disabled={loading || mappedRequired < requiredFields.length}
            className="btn-primary-custom"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving Mappings...
              </>
            ) : (
              <>
                Continue to Validation
                <i className="fas fa-arrow-right ms-2"></i>
              </>
            )}
          </Button>
        </div>

        {/* Template Modal */}
        <Modal show={showTemplateModal} onHide={() => this.setState({ showTemplateModal: false })}>
          <Modal.Header closeButton>
            <Modal.Title>Load Mapping Template</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Select Template</Form.Label>
              <Form.Select
                value={this.state.selectedTemplate}
                onChange={(e) => this.setState({ selectedTemplate: e.target.value })}
              >
                <option value="">-- Choose a template --</option>
                {templates.map(template => (
                  <option key={template.template_Id} value={template.template_Id}>
                    {template.template_name}
                    {template.template_description && ` - ${template.template_description}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => this.setState({ showTemplateModal: false })}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.loadTemplate}
              disabled={!this.state.selectedTemplate}
            >
              Load Template
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Help Information */}
        <div className="row mt-4">
          <div className="col-12">
            <Alert variant="light" className="border">
              <h6 className="alert-heading">
                <i className="fas fa-lightbulb me-2"></i>
                Tips for Field Mapping
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <ul className="small mb-0">
                    <li><strong>Auto-Suggest:</strong> Click the button to automatically match similar field names</li>
                    <li><strong>Required Fields:</strong> All required fields (marked with *) must be mapped</li>
                    <li><strong>System Fields:</strong> Fields like createdBy, status are automatically handled</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="small mb-0">
                    <li><strong>Duplicate Check:</strong> Select one or multiple fields for unique combination checking</li>
                    <li><strong>Example:</strong> Select both "employeeid" and "membername" to ensure unique employee-member pairs</li>
                    <li><strong>Templates:</strong> Save your mapping configuration for future imports</li>
                  </ul>
                </div>
              </div>
            </Alert>
          </div>
        </div>
      </div>
    );
  }
}